import type { RemoteThreadListAdapter } from '@assistant-ui/react';
import { createAssistantStream } from 'assistant-stream';
import type { TFunction } from 'i18next';
import { api } from './api';
import { env } from './env';

type ListResult = Awaited<ReturnType<RemoteThreadListAdapter['list']>>;
type InitResult = Awaited<ReturnType<RemoteThreadListAdapter['initialize']>>;
type FetchResult = Awaited<ReturnType<RemoteThreadListAdapter['fetch']>>;

export interface CreateThreadListAdapterArgs {
  /**
   * Translator used to compute the title for newly-minted threads. Passed as
   * a function (not a string) so the closure reads the latest `t` binding
   * after a language switch (i18next mutates `t` on switch).
   */
  t: () => TFunction;
  /** Current i18n language (e.g. `de`, `en-US`). */
  locale: () => string;
  /** Called after `initialize` so the URL can be rewritten to the new id. */
  onInitialized?: (remoteId: string) => void;
  /** Called after `delete` so the router can leave a deleted thread's URL. */
  onDeleted?: (remoteId: string) => void;
  /**
   * Trigger a fresh `list()` fetch on the assistant-ui runtime — wraps
   * `runtime.threads.reload()`. Called by `rename` / `delete` / `generateTitle`
   * to keep the sidebar's `updatedAt`-driven sort in sync with the server
   * after a mutation. `initialize` does NOT call this: the runtime's
   * `updateStatusReducer` already prepends the freshly-minted thread to the
   * top of `threadIds`.
   *
   * Optional. When undefined the adapter still functions; the sidebar just
   * won't refresh on mutation.
   */
  reload?: () => void;
}

function langToLocale(lang: string): 'en' | 'de' {
  return lang?.startsWith('de') ? 'de' : 'en';
}

/**
 * Map the API's `/threads` REST surface onto assistant-ui's
 * `RemoteThreadListAdapter`. assistant-ui drives all sidebar state from
 * this — list / create / rename / delete / fetch.
 *
 * The backend has no archive endpoint, so `archive`/`unarchive` are no-ops.
 */
export function createThreadListAdapter(
  args: CreateThreadListAdapterArgs,
): RemoteThreadListAdapter {
  return {
    async list(): Promise<ListResult> {
      const { data, error } = await api.GET('/threads');
      if (error) throw new Error('Failed to list threads');
      const threads: ListResult['threads'] = data.items.map((t) => ({
        status: 'regular' as const,
        remoteId: t.id,
        title: t.title,
      }));
      return { threads };
    },

    async initialize(localId: string): Promise<InitResult> {
      const { data, error } = await api.POST('/threads', {
        // The assistant-ui draft id is stable across retries of the same mint,
        // so it serves as the idempotency key: a double-submit collapses to a
        // single thread server-side instead of spawning duplicates.
        headers: { 'Idempotency-Key': localId },
        body: {
          title: args.t()('chat.sidebar.newThreadTitle'),
          locale: langToLocale(args.locale()),
        },
      });
      if (error) throw new Error('Failed to create thread');
      args.onInitialized?.(data.id);
      // No reload() here: the runtime's `updateStatusReducer` already prepends
      // the new thread to `threadIds` optimistically. A server re-fetch would
      // flicker and could race the still-in-flight persist of the first turn.
      return { remoteId: data.id, externalId: undefined };
    },

    async rename(remoteId: string, newTitle: string): Promise<void> {
      const { error } = await api.PATCH('/threads/{threadId}', {
        params: { path: { threadId: remoteId } },
        body: { title: newTitle },
      });
      if (error) throw new Error('Failed to rename thread');
      args.reload?.();
    },

    async delete(remoteId: string): Promise<void> {
      const { error } = await api.DELETE('/threads/{threadId}', {
        params: { path: { threadId: remoteId } },
      });
      if (error) throw new Error('Failed to delete thread');
      args.onDeleted?.(remoteId);
      args.reload?.();
    },

    async archive(): Promise<void> {
      // Backend has no archive endpoint. No-op pass-through.
    },

    async unarchive(): Promise<void> {
      // No-op; see `archive` above.
    },

    async fetch(remoteId: string): Promise<FetchResult> {
      const { data, error } = await api.GET('/threads/{threadId}', {
        params: { path: { threadId: remoteId } },
      });
      if (error) throw new Error('Failed to fetch thread');
      return { status: 'regular', remoteId: data.id, title: data.title };
    },

    /**
     * Invoked by assistant-ui after the first assistant turn completes.
     * Delegates to `POST /threads/:id/title`, which generates a short title
     * via the LLM and PATCHes the thread row server-side so it survives a
     * refresh.
     *
     * On endpoint failure (network, 502, 4xx), falls back to a truncation of
     * the first user message so the sidebar never sticks on the placeholder.
     * The fallback is local-only; the server title is left unchanged and the
     * user can rename manually.
     */
    async generateTitle(remoteId, messages) {
      const fallbackTitle = (): string => {
        const firstUser = messages.find((m) => m.role === 'user');
        if (!firstUser) return args.t()('chat.sidebar.newThreadTitle');
        const text = firstUser.content
          .filter((c): c is { type: 'text'; text: string } => c.type === 'text')
          .map((c) => c.text)
          .join(' ')
          .trim();
        if (!text) return args.t()('chat.sidebar.newThreadTitle');
        return text.length > 60 ? `${text.slice(0, 57)}…` : text;
      };

      let titleText: string;
      try {
        const url = `${env.VITE_API_BASE_URL}/threads/${encodeURIComponent(remoteId)}/title`;
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'x-request-id': crypto.randomUUID() },
        });
        if (!res.ok) {
          throw new Error(`title endpoint returned ${res.status}`);
        }
        const raw = (await res.text()).trim();
        titleText = raw.length > 0 ? raw : fallbackTitle();
      } catch {
        titleText = fallbackTitle();
      } finally {
        // Title generation touched the thread row server-side; pull the fresh
        // order so the now-titled thread sorts by the server's updatedAt.
        args.reload?.();
      }

      return createAssistantStream((controller: { appendText: (text: string) => void }) => {
        controller.appendText(titleText);
      });
    },
  };
}
